// ============================================================
// Network containment for the demo (showcase) mode.
//
// WHY A FETCH INTERCEPTOR AND NOT PER-CALL GUARDS:
// demo mode runs the REAL portals — InvestorPortal alone has 29 direct
// fetch() calls plus a makeAPIRequest helper, and it hands API_URL down to
// PropertyView, DocumentsView, PortfolioView, ChatWidget and more, each with
// their own calls. Gating those one by one means the safety of the whole
// feature rests on my having found every single one. Miss a single call and
// an unauthenticated demo session starts talking to the live backend.
//
// So containment sits at the one choke point every call must pass through.
// The app uses fetch exclusively — no axios, no XMLHttpRequest anywhere in
// src/ (checked) — so patching window.fetch for the lifetime of the demo
// view catches 100% of traffic, including calls added later by someone who
// never reads this file.
//
// WHAT BLOCKED CALLS RETURN: an empty-but-well-formed 200 JSON payload, not a
// rejection. Components then take their "no data" path and render empty
// states, instead of throwing errors and filling the screen with red toasts
// during a management demo.
//
// Same-origin non-API requests (the logo, the manifest, PDF worker assets)
// pass through untouched, otherwise the app would visibly break.
// ============================================================

/**
 * Shape blocked calls resolve to. Deliberately over-provided: different call
 * sites destructure different keys (`data`, `units`, `documents`, …), and a
 * missing key would throw where an empty array renders an empty list.
 */
const EMPTY_PAYLOAD = {
  success: true,
  demo: true,
  data: {},
  units: [],
  documents: [],
  notifications: [],
  investors: [],
  plans: [],
  items: [],
  rows: [],
  messages: [],
  total: 0,
};

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url || '';
}

/**
 * True for requests that must still reach the network for the app to render:
 * same-origin static assets. Anything cross-origin, and anything that looks
 * like an API call, is blocked.
 */
function isAllowedThrough(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    // Same-origin API paths (a relative '/api/…' call) must still be blocked.
    if (url.pathname.includes('/api/')) return false;
    return true;
  } catch {
    // Unparseable — treat as untrusted and block.
    return false;
  }
}

let installed = 0;

/**
 * Blocks all API traffic for as long as the demo view is mounted.
 * Returns the uninstall function — call it on unmount.
 *
 * Reference-counted, so nested demo views (hub → portal) cannot leave the
 * real fetch permanently replaced if they unmount out of order.
 */
export function installDemoNetworkBlock(): () => void {
  installed += 1;

  if (installed === 1) {
    const original = window.fetch.bind(window);
    (window as any).__demoRealFetch = original;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = urlOf(input);
      if (isAllowedThrough(url)) return original(input as any, init);

      if (process.env.NODE_ENV !== 'production') {
        console.info('[demo] blocked network call:', url);
      }
      return new Response(JSON.stringify(EMPTY_PAYLOAD), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    };
  }

  let released = false;
  return () => {
    if (released) return;      // idempotent — React can call cleanup twice
    released = true;
    installed -= 1;
    if (installed === 0) {
      const original = (window as any).__demoRealFetch;
      if (original) {
        window.fetch = original;
        delete (window as any).__demoRealFetch;
      }
    }
  };
}

/** True while the block is active — used by tests and the debug banner. */
export function isDemoNetworkBlocked(): boolean {
  return installed > 0;
}
